// src/components/differential/DifferentialKing.jsx
import { f1 } from '../../data/dataUtils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useManagerContext } from '../../context/DataContext';

const DifferentialKing = () => {
  const [isChestOpen, setIsChestOpen] = useState(false);
  const [revealedGems, setRevealedGems] = useState(0);
  const navigate = useNavigate();
  
  const { getManagerData } = useManagerContext();
  const managerData = getManagerData();
  
  // If we don't have real data yet, use mock data
  const differentialData = managerData?.differentialPlayers?.summary || {
    Differential_Points: 124,
    Differential_Percentage: 14.2,
    Differential_Count: 8,
    Best_Differential_Player: "Rodrigo",
    Best_Differential_Points: 17,
    Best_Differential_Ownership: 4.8,
    Best_Differential_GW: 24
  };
  
  // Get detailed differential players
  const detailedDifferentials = managerData?.differentialPlayers?.details || [
    { player_name: "Rodrigo", ownership: 4.8, points: 17, gw: 24 },
    { player_name: "Diogo Jota", ownership: 3.2, points: 12, gw: 8 },
    { player_name: "Palmer", ownership: 4.9, points: 14, gw: 16 },
    { player_name: "Saka", ownership: 4.7, points: 13, gw: 32 }
  ];
  
  // Sort differentials by points (highest first)
  const sortedDifferentials = [...detailedDifferentials].sort((a, b) => b.points - a.points);
  
  // Top 4 differentials for display
  const topDifferentials = sortedDifferentials.slice(0, 4);
  
  const openChest = () => {
    setIsChestOpen(true);
    
    // Reveal gems one by one with a delay
    let count = 0;
    const interval = setInterval(() => {
      count++;
      setRevealedGems(count);
      if (count >= topDifferentials.length) {
        clearInterval(interval);
      }
    }, 800);
  };
  
  // Navigation functions
  const goToJourneySelector = () => {
    navigate('/journey-selector');
  };
  
  const goToMyJourney = () => {
    // Navigate to the My Profile tab in My Journey
    navigate('/my-journey', { state: { activeTab: 'profile' } });
  };
  
  return (
    <div className="h-full flex flex-col items-center justify-between py-6 px-4 text-white" style={{ background: 'linear-gradient(135deg, #000006 0%, #0a1628 100%)' }}>
      <h2 className="text-2xl font-bold text-emerald-300 mb-2">Your Differential Genius</h2>
      
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm">
        <div className="w-64 h-64 relative mb-8 flex items-center justify-center">
          {/* Treasure chest (closed) */}
          {!isChestOpen && (
            <div 
              className="w-56 h-48 bg-amber-800 rounded-lg relative cursor-pointer transform transition-transform hover:scale-105"
              onClick={openChest}
            >
              {/* Chest lid */}
              <div className="absolute top-0 left-0 w-full h-16 bg-amber-700 rounded-t-lg border-b-4 border-amber-900 flex items-center justify-center">
                <div className="w-12 h-8 bg-yellow-600 rounded-md border-2 border-yellow-800"></div>
              </div>
              
              {/* Chest details */}
              <div className="absolute bottom-0 left-0 w-full h-8 border-t-2 border-amber-900"></div>
              
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-white text-center p-2 mt-6">
                  <p className="font-bold">TAP TO REVEAL</p>
                  <p>YOUR HIDDEN GEMS</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Treasure chest (open) */}
          {isChestOpen && (
            <div className="w-56 h-48 bg-amber-800 rounded-lg relative transform transition-all duration-500">
              {/* Chest lid (open) */}
              <div className="absolute top-0 left-0 w-full h-16 bg-amber-700 rounded-t-lg border-b-4 border-amber-900 origin-bottom transform -translate-y-14 -rotate-180 flex items-center justify-center">
                <div className="w-12 h-8 bg-yellow-600 rounded-md border-2 border-yellow-800 origin-center transform rotate-180"></div>
              </div>
              
              {/* Treasure inside */}
              <div className="absolute top-4 left-0 w-full h-36 flex items-center justify-center overflow-hidden">
                <div className="w-48 h-32 bg-gradient-to-b from-yellow-300 to-yellow-500 rounded-md border-2 border-yellow-600 flex flex-wrap justify-center items-center p-2">
                  {topDifferentials.slice(0, revealedGems).map((diff, index) => (
                    <div key={index} className="m-1 p-2 bg-white rounded-md shadow-md flex flex-col items-center transform animate-bounce">
                      <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center mb-1">
                        <span className="text-xs text-emerald-600 font-bold">{diff.points}</span>
                      </div>
                      <div className="text-xs font-bold text-gray-800">{diff.player_name}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Chest details */}
              <div className="absolute bottom-0 left-0 w-full h-8 border-t-2 border-amber-900"></div>
            </div>
          )}
          
          {/* Glowing effect when open */}
          {isChestOpen && (
            <div className="absolute inset-0 bg-yellow-400 opacity-20 rounded-full filter blur-xl animate-pulse"></div>
          )}
        </div>
        
        {!isChestOpen && (
          <div className="text-center text-gray-300 mb-4">
            <p>Tap the treasure chest to reveal your differential picks</p>
          </div>
        )}
        
        {isChestOpen && revealedGems > 0 && (
          <div className="text-center mb-6 animate-fade-in">
            <p className="text-lg font-bold mb-1">
              Your best differential: {differentialData.Best_Differential_Player} ({f1(differentialData.Best_Differential_Ownership)}%)
            </p>
            <p className="text-gray-300">
              scored {differentialData.Best_Differential_Points} points in GW{differentialData.Best_Differential_GW}!
            </p>
            
            {revealedGems === topDifferentials.length && (
              <div className="mt-4 p-3 bg-emerald-900/50 backdrop-blur-sm rounded-lg border border-emerald-700">
                <p className="font-semibold text-emerald-300">
                  You got {differentialData.Differential_Points} points from differentials!
                </p>
                <p className="text-sm text-emerald-400">
                  That's {f1(differentialData.Differential_Percentage)}% of your total points.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="w-full max-w-md flex justify-between items-center">
        <button 
          onClick={goToJourneySelector}
          className="flex items-center justify-center px-4 py-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors"
        >
          <ChevronLeft size={18} className="mr-2" />
          Back to Journey Selector
        </button>
        
        <button 
          onClick={goToMyJourney}
          className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
        >
          Go to My Journey
          <ChevronRight size={18} className="ml-2" />
        </button>
      </div>
    </div>
  );
};

export default DifferentialKing;